import React from 'react';
import { User, Shield, Mail, Building2, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

export function Profile() {
  const { user, permissions, loading, isFounder } = useAuth();

  // Show loading state
  if (loading) {
    return (
      <div className="page" data-testid="profile-page">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '300px' 
        }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--accent-primary)' }} />
        </div>
      </div>
    );
  }

  // Get role display from permissions
  const getUserRole = () => {
    if (isFounder()) return 'Founder';
    if (permissions?.organisations?.length > 0) {
      const orgRole = permissions.organisations[0].role;
      return orgRole.charAt(0) + orgRole.slice(1).toLowerCase();
    }
    return 'User';
  };

  // Get organization name (if available from permissions context)
  const getOrgName = () => {
    // This would ideally come from a separate org fetch, but for now we use what's available
    if (isFounder()) return 'SKB Interiors';
    return 'Your Organisation';
  };

  return (
    <div className="page" data-testid="profile-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Profile</h1>
          <p>Manage your personal information</p>
        </div>
      </div>

      <div className="glass-panel">
        {/* User Avatar Section */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-lg)',
          marginBottom: 'var(--space-xl)',
          paddingBottom: 'var(--space-lg)',
          borderBottom: '1px solid var(--glass-border)'
        }}>
          <div style={{
            width: '72px',
            height: '72px',
            borderRadius: 'var(--radius-full)',
            background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.5rem',
            fontWeight: '600',
            color: 'white'
          }}>
            {user?.name ? user.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'U'}
          </div>
          <div>
            <div style={{ 
              fontSize: '1.25rem', 
              fontWeight: '600', 
              color: 'var(--text-primary)',
              marginBottom: '4px'
            }}>
              {user?.name || 'User'}
            </div>
            <div style={{ 
              fontSize: '0.875rem', 
              color: 'var(--text-muted)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-sm)'
            }}>
              {isFounder() && (
                <span style={{
                  background: 'var(--accent-primary)',
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: 'var(--radius-full)',
                  fontSize: '0.75rem',
                  fontWeight: '500'
                }}>
                  Founder
                </span>
              )}
              {user?.email}
            </div>
          </div>
        </div>

        {/* User Details Grid */}
        <div className="detail-grid">
          <div className="detail-item">
            <div className="detail-item-label" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
              <User size={14} style={{ color: 'var(--text-muted)' }} />
              Name
            </div>
            <div className="detail-item-value" data-testid="profile-name">
              {user?.name || 'Not set'}
            </div>
          </div>
          <div className="detail-item">
            <div className="detail-item-label" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
              <Mail size={14} style={{ color: 'var(--text-muted)' }} />
              Email
            </div>
            <div className="detail-item-value" data-testid="profile-email">
              {user?.email || 'Not set'}
            </div>
          </div>
          <div className="detail-item">
            <div className="detail-item-label" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
              <Building2 size={14} style={{ color: 'var(--text-muted)' }} />
              Organisation
            </div>
            <div className="detail-item-value" data-testid="profile-org">
              {getOrgName()}
            </div>
          </div>
          <div className="detail-item">
            <div className="detail-item-label" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
              <Shield size={14} style={{ color: 'var(--text-muted)' }} />
              Role
            </div>
            <div className="detail-item-value" data-testid="profile-role">
              {getUserRole()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;
